/**
 * Where the cues come from, into one store every face reads:
 *  - live (kiosk, operator): the backend's `/avatar/events`, the same stream the three.js head reads;
 *  - replay: the recorded answers — each one's `emoji` (or the emoji it starts with) becomes a cue.
 * The app picks one by role (app/compose.ts).
 */
import { createStore, type Store } from '../core/store';
import type { Conversation } from '../core/conversation';
import { report } from '../core/diagnostics';
import { cueName, parseCue, type FaceCue } from './cues';

export const AVATAR_EVENTS_URL = '/avatar/events';

export function createCueStore(): Store<FaceCue | null> {
  return createStore<FaceCue | null>('face-cue', null);
}

/** Follows `/avatar/events` (EventSource reconnects by itself); returns the stop. */
export function followAvatarEvents(cues: Store<FaceCue | null>, url = AVATAR_EVENTS_URL): () => void {
  if (typeof EventSource === 'undefined') return () => {};
  const source = new EventSource(url);
  source.onmessage = (e: MessageEvent<string>) => {
    try {
      const cue = parseCue(JSON.parse(e.data), Date.now());
      if (cue) cues.set(cue);
    } catch {
      report('warn', 'avatar', `not a cue on ${url}: ${e.data.slice(0, 80)}`);
    }
  };
  return () => source.close();
}

const LEADING_EMOJI = /^\s*(\p{Extended_Pictographic}️?)/u;

/** The cue an answer carries: its `emoji` field, else the emoji its text starts with. */
export function cueOfAnswer(emoji: string | undefined, text: string, at: number): FaceCue | null {
  const found = emoji || LEADING_EMOJI.exec(text)?.[1];
  return found ? { emoji: found, name: cueName(found), mode: 'once', at } : null;
}

/** Turns each new answer of the conversation into a cue (replays); returns the stop. */
export function cuesFromAnswers(conversation: Store<Conversation>, cues: Store<FaceCue | null>): () => void {
  let seen = new Set(conversation.get().entries.map((e) => e.id));
  return conversation.subscribe(() => {
    const entries = conversation.get().entries;
    for (const e of entries) {
      if (seen.has(e.id) || e.role !== 'assistant') continue;
      const cue = cueOfAnswer(e.message.emoji, e.text, Date.now());
      if (cue) cues.set(cue);
    }
    seen = new Set(entries.map((e) => e.id));
  });
}
